import { ActionIcon, Button, Group, Modal, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPencil } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useRenameDocument } from 'src/data-capture/hooks/useRenameDocument';
import { useFlags } from 'src/feature-flags/FlagsHook';

type RenameDocumentControlProps = {
  fileId: number;
  currentName: string;
  variant?: 'default' | 'chip';
};

export default function RenameDocumentControl({
  fileId,
  currentName,
  variant = 'default',
}: RenameDocumentControlProps) {
  const { flagEnabledDocumentRename } = useFlags();
  const [opened, { open, close }] = useDisclosure(false);
  const [name, setName] = useState(currentName);
  const { renameDocument, isRenaming, error, reset } = useRenameDocument();

  useEffect(() => {
    if (opened) {
      setName(currentName);
      reset();
    }
  }, [opened, currentName, reset]);

  if (!flagEnabledDocumentRename) return null;

  const handleOpen = (event: React.MouseEvent) => {
    event.stopPropagation();
    open();
  };

  const handleSave = async () => {
    try {
      await renameDocument(fileId, name);
      close();
    } catch {
      // The error is surfaced in the modal; keep it open so the user can adjust and retry.
    }
  };

  return (
    <>
      <Tooltip label="Rename" withArrow>
        <ActionIcon
          variant="subtle"
          {...iconStyles[variant]}
          aria-label="Rename document"
          onClick={handleOpen}
        >
          <IconPencil size={16} stroke={variant === 'chip' ? 1.5 : undefined} />
        </ActionIcon>
      </Tooltip>
      <Modal opened={opened} onClose={close} title="Rename document" centered>
        <Stack gap="md">
          <TextInput
            label="Document name"
            value={name}
            onChange={event => setName(event.target.value)}
            data-autofocus
          />
          {error && (
            <Text c="red" size="sm">
              Couldn&apos;t rename this document. Keep the same file type and try again.
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={close} disabled={isRenaming}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={isRenaming}
              disabled={!name.trim() || name.trim() === currentName}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

const iconStyles = {
  default: { color: 'gray.7', size: 'sm' },
  chip: { c: 'brand.7', bg: 'brand.0', size: 20, radius: 8 },
} as const;
